import React, { Fragment, useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Card, CardBody, CardHeader } from "reactstrap";
import { Btn, H5 } from "../../../../AbstractElements";
import DataTable from "react-data-table-component";
import axios from "axios";
import config from "../../../../config";

/* STATUS BADGE */
const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const colors = {
        MATCHED: "success",
        NOT_MATCHED: "secondary",
        RECONCILED: "warning",
        PENDING: "dark"
    };

    return (
        <span
            className={`badge badge-light-${colors[status] || "secondary"}`}
            style={{ fontSize: "12px", padding: "6px 12px", borderRadius: "6px", fontWeight: "500" }}
        >
            {t(status.replace("_", " "))}
        </span>
    );
};

/* MONEY FORMAT */
const fmt = (n) =>
    n != null
        ? Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "—";

/* DATE FORMAT */
const fmtDate = (d) =>
    d
        ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : "—";

/* INFO FIELD */
const InfoField = ({ label, value }) => (
    <Col md="4" className="mb-3">
        <p className="mb-1 text-muted f-12">{label}</p>
        <p className="mb-0 f-w-600">{value || "—"}</p>
    </Col>
);

const TenantAuditDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const token = localStorage.getItem("sessiontoken");
    const configheader = { headers: { Authorization: `${token}` } };
    const TOLERANCE = Number(localStorage.getItem("auditTolerance") || 0);

    const [tenant, setTenant] = useState(location.state?.tenant || null);
    const [loading, setLoading] = useState(!location.state?.tenant);

    const isLocked = location.state?.isLocked || false;
    const auditPeriodId = location.state?.auditPeriodId;
    const monthLabel = location.state?.monthLabel;
    const selectedYear = location.state?.selectedYear;

    const monthNames = [
        "", "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    /* FETCH TENANT */
    useEffect(() => {

        setLoading(true);

        axios
            .get(`${config.hostname}/audit/tenant/${id}`, configheader)
            .then((res) => {
                setTenant(res.data);
            })
            .catch(console.error)
            .finally(() => setLoading(false));

    }, [id, location.key]);

    useEffect(() => {
        if (location.state?.updatedTenant) {
            setTenant(location.state.updatedTenant);
        }
    }, [location.state]);

    const sys = tenant?.audit_values?.SYSTEM;
    const upl = tenant?.audit_values?.UPLOADED;

    /* COMPARISON DATA */
    const comparisonData = useMemo(() => {
        if (!tenant) return [];

        const metrics = [
            { key: "gross_sales", label: "Gross Sales" },
            { key: "net_sales", label: "Net Sales" },
            { key: "tax", label: "Tax" },
            { key: "discount", label: "Discount" },
            { key: "returns", label: "Returns" }
        ];

        return metrics.map((m) => {
            const sysVal = sys?.[m.key];
            const uplVal = upl?.[m.key];

            const diff = sysVal != null && uplVal != null ? Number((uplVal - sysVal).toFixed(2)) : null;
            const withinTolerance = diff != null && Math.abs(diff) <= TOLERANCE;

            return {
                metric: t(m.label),
                system: <div className="money">{fmt(sysVal)}</div>,
                uploaded: <div className="money">{fmt(uplVal)}</div>,
                difference: diff != null
                    ? <div className={`money ${withinTolerance ? "diff-zero" : "diff-pos"}`}>{fmt(diff)}</div>
                    : "—",
                match: diff == null
                    ? "—"
                    : withinTolerance
                        ? <span className="text-success f-w-700">✓</span>
                        : <span className="text-danger f-w-700">✗</span>
            };
        });
    }, [tenant, t]);

    const columns = [
        { name: t('Metric'), selector: (row) => row.metric, sortable: true, minWidth: "220px" },
        { name: t('System'), selector: (row) => row.system, sortable: true, left: true, minWidth: "160px" },
        { name: t('Audit'), selector: (row) => row.uploaded, sortable: true, left: true, minWidth: "160px" },
        { name: t('Difference'), selector: (row) => row.difference, sortable: true, left: true, minWidth: "160px" },
        { name: t('Match'), selector: (row) => row.match, left: true, width: "120px" }
    ];

    const customStyles = {
        headCells: { style: { fontSize: "13px", fontWeight: "600" } },
        cells: { style: { fontSize: "13px" } }
    };

    const styles = `
        .money{ text-align:right; white-space:nowrap; }
        .diff-pos{ color:#d32f2f; font-weight:600; }
        .diff-zero{ color:#2e7d32; font-weight:600; }
    `;

    const handleBack = () => {
        navigate(`${process.env.PUBLIC_URL}/admin/audit/default`, {
            state: {
                property_id: tenant?.property_id,
                property_name: tenant?.property_name,
                month: monthLabel,
                year: selectedYear
            }
        });
    };

    const handleReconcile = () => {
        navigate(`${process.env.PUBLIC_URL}/admin/audit/reconciliation/${id}`, {
            state: { tenant, auditPeriodId, monthLabel, selectedYear, from: "detail" }
        });
    };

    if (loading) return <div className="text-center p-5">{t('Loading tenant audit data...')}</div>;
    if (!tenant) return <div className="text-center p-5">{t('Tenant audit record not found.')}</div>;

    return (
        <Fragment>

            <style>{styles}</style>

            <Card>
                <CardBody>
                    <Row className="align-items-center">
                        <Col md="8">
                            <H5 className="mb-1">{tenant.tenant_name} – {monthNames[monthLabel] ? t(monthNames[monthLabel]) : ''} {selectedYear}</H5>
                            <StatusBadge status={tenant.status} />
                        </Col>

                        <Col md="4" className="text-end">
                            {tenant.status === "NOT_MATCHED" && !isLocked && (
                                <Btn attrBtn={{ color: "primary", className: "me-3", onClick: handleReconcile }}>
                                    {t('Reconcile')}
                                </Btn>
                            )}

                            <Btn attrBtn={{ color: "primary", onClick: handleBack }}>
                                {t('Back')}
                            </Btn>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            <Row>

                <Col xl="12">
                    <Card>
                        <CardHeader><H5>{t('Tenant Information')}</H5></CardHeader>
                        <CardBody>
                            <Row>
                                <InfoField label={t('Tenant Name')} value={tenant.tenant_name} />
                                <InfoField label={t('Tenant Code')} value={tenant.tenant_code} />
                                <InfoField label={t('Outlet Code')} value={tenant.outlet_code} />
                                <InfoField label={t('Property')} value={tenant.property_name} />
                                <InfoField label={t('Last Audited')} value={fmtDate(tenant?.audit_values?.UPLOADED?.updated_at)} />

                                {tenant.comment && <InfoField label={t('Comment')} value={tenant.comment} />}
                                {tenant.discrepancy_remarks && (
                                    <InfoField label={t('Discrepancy Remarks')} value={tenant.discrepancy_remarks} />
                                )}
                            </Row>
                        </CardBody>
                    </Card>
                </Col>

                <Col xl="12">
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <H5>{t('System vs Audited Comparison')}</H5>
                        </CardHeader>

                        <CardBody>
                            <DataTable
                                data={comparisonData}
                                columns={columns}
                                striped
                                highlightOnHover
                                pagination={false}
                                customStyles={customStyles}
                            />
                        </CardBody>
                    </Card>
                </Col>

            </Row>

        </Fragment>
    );
};

export default TenantAuditDetail;